import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Switch,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Divider,
  Chip,
  InputAdornment,
  OutlinedInput
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance,
  CreditCard,
  Wallet,
  Search as SearchIcon,
  Sort as SortIcon,
  FilterList as FilterIcon,
  Archive as ArchiveIcon,
  Restore as RestoreIcon
} from '@mui/icons-material';
import { useSupabase } from '../contexts/SupabaseContext';
import CardNetworkLogo from '../components/CardNetworkLogo';
import GuidedTour from '../components/tour/GuidedTour';
import TourButton from '../components/tour/TourButton';
import useTour from '../components/tour/useTour';
import { TOURS } from '../components/tour/tours';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

const walletTypes = [
  { value: 'bank', label: 'Bank Account', icon: <AccountBalance /> },
  { value: 'credit', label: 'Credit Card', icon: <CreditCard /> },
  { value: 'cash', label: 'Cash Wallet', icon: <Wallet /> }
];

const cardVariants = [
  { value: 'visa', label: 'Visa' },
  { value: 'mastercard', label: 'Mastercard' },
  { value: 'rupay', label: 'RuPay' },
  { value: 'amex', label: 'American Express' }
];

// style for  number input
const numberInputStyle = {
  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
};

// Card network and bank logos, served from public/logos (self-hosted so they can't break)
const logos = {
  cards: {
    visa: '/logos/visa.svg',
    mastercard: '/logos/mastercard.svg',
    rupay: '/logos/rupay.svg',
    amex: '/logos/amex.svg'
  },
  banks: {
    hdfc: '/logos/hdfc.svg',
    sbi: '/logos/sbi.svg',
    icici: '/logos/icici.svg',
    axis: '/logos/axis.svg'
  }
};

// Add this to the existing bank logos
const bankOptions = [
  { value: 'sbi', label: 'State Bank of India', logo: logos.banks.sbi },
  { value: 'hdfc', label: 'HDFC Bank', logo: logos.banks.hdfc },
  { value: 'icici', label: 'ICICI Bank', logo: logos.banks.icici },
  { value: 'axis', label: 'Axis Bank', logo: logos.banks.axis },
  { value: 'custom', label: 'Custom Bank', logo: null }
];

// Styled components
const StyledCard = styled(Card)(({ theme, type, variant }) => ({
  position: 'relative',
  overflow: 'hidden',
  background: type === 'credit' 
    ? variant === 'visa' 
      ? 'linear-gradient(135deg, #1A1F71 0%, #2B3595 100%)'
      : variant === 'mastercard'
      ? 'linear-gradient(135deg, #2B3595 0%, #4B56B8 100%)'
      : variant === 'rupay'
      ? 'linear-gradient(135deg, #097DC6 0%, #ED752E 100%)'
      : variant === 'amex'
      ? 'linear-gradient(135deg, #006FCF 0%, #10509E 100%)'
      : `linear-gradient(135deg, 
          ${alpha(theme.palette.primary.dark, 0.95)}, 
          ${alpha(theme.palette.primary.main, 0.85)})`
    : type === 'bank'
    ? 'linear-gradient(135deg, #334155 0%, #0f172a 100%)'
    : 'linear-gradient(135deg, #059669 0%, #065f46 100%)',
  color: '#fff',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: 'none',
  borderRadius: '16px',
  minHeight: '220px',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.3)'
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: 'auto',
    '& .MuiCardContent-root': {
      padding: theme.spacing(1.5),
    },
    '& .MuiCardActions-root': {
      padding: theme.spacing(0.5),
    }
  }
}));

const CardWatermark = styled('div')(({ theme, logo }) => ({
  position: 'absolute',
  right: '-60px',
  bottom: '-60px',
  width: '280px',
  height: '280px',
  backgroundImage: `url(${logo})`,
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  opacity: 0.06,
  pointerEvents: 'none',
  zIndex: 0,
  transition: 'all 0.3s ease',
  '$:hover': {
    opacity: 0.12
  }
}));

const CardChip = styled('div')(({ theme }) => ({
  width: '45px',
  height: '35px',
  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
  borderRadius: '8px',
  marginBottom: theme.spacing(1),
  position: 'relative',
  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '75%',
    height: '55%',
    background: 'linear-gradient(135deg, #FFE57F 0%, #FFB74D 100%)',
    borderRadius: '4px'
  }
}));

const BankLogo = styled('img')(({ theme }) => ({
  height: 35,
  width: 'auto',
  maxWidth: 100,
  objectFit: 'contain',
  marginLeft: 'auto',
  opacity: 0.9,
  transition: 'all 0.3s ease',
  '&:hover': {
    opacity: 1,
    transform: 'scale(1.05)'
  }
}));

const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  borderRadius: '20px',
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.08)}, 
    ${alpha(theme.palette.primary.light, 0.12)})`,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(2),
    padding: theme.spacing(2)
  }
}));

const StyledChip = styled(Chip)(({ theme, selected }) => ({
  borderRadius: '12px',
  padding: theme.spacing(0.5, 1),
  backgroundColor: selected 
    ? alpha(theme.palette.primary.main, 0.12)
    : 'transparent',
  border: `1px solid ${
    selected
      ? theme.palette.primary.main
      : alpha(theme.palette.divider, 0.8)
  }`,
  '& .MuiChip-label': {
    color: selected 
      ? theme.palette.primary.main
      : theme.palette.text.primary,
    fontWeight: selected ? 600 : 400
  },
  '& .MuiChip-icon': {
    color: selected 
      ? theme.palette.primary.main
      : theme.palette.text.secondary
  },
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    transform: 'translateY(-2px)'
  }
}));

const AddButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.2, 3),
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 600,
  background: `linear-gradient(135deg, 
    ${theme.palette.primary.main}, 
    ${theme.palette.primary.dark})`,
  boxShadow: `0 8px 16px -4px ${alpha(theme.palette.primary.main, 0.25)}`,
  '&:hover': {
    background: `linear-gradient(135deg, 
      ${theme.palette.primary.dark}, 
      ${theme.palette.primary.main})`,
    boxShadow: `0 12px 20px -6px ${alpha(theme.palette.primary.main, 0.35)}`,
    transform: 'translateY(-2px)'
  }
}));

const SearchBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  borderRadius: '16px',
  background: alpha(theme.palette.background.paper, 0.6),
  backdropFilter: 'blur(10px)',
  '& > *': {
    width: '100%'
  },
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    '& > *': {
      width: 'auto'
    }
  }
}));

const StyledOutlinedInput = styled(OutlinedInput)(({ theme }) => ({
  borderRadius: '12px',
  backgroundColor: alpha(theme.palette.common.white, 0.06),
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.text.primary, 0.1),
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.text.primary, 0.2),
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.05)}`
  }
}));

// Format currency helper
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

// Update the LogoManagement component to receive supabase as a prop
const LogoManagement = ({ value, onChange, supabase }) => {
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [logoUrl, setLogoUrl] = useState(value || '');
  const fileInputRef = React.useRef();

  const handleUrlSubmit = () => {
    onChange(logoUrl);
    setShowUrlInput(false);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const { data, error } = await supabase.storage
          .from('bank-logos')
          .upload(`${Date.now()}-${file.name}`, file);

        if (error) throw error;

        const { data: { publicUrl } } = supabase.storage
          .from('bank-logos')
          .getPublicUrl(data.path);

        onChange(publicUrl);
      } catch (error) {
        console.error('Error uploading logo:', error);
        alert('Error uploading logo');
      }
    }
  };

  return (
    <Box>
      {showUrlInput ? (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            label="Logo URL"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
          />
          <Button onClick={handleUrlSubmit} variant="contained" size="small">
            Save
          </Button>
          <Button 
            onClick={() => setShowUrlInput(false)} 
            variant="outlined" 
            size="small"
          >
            Cancel
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {value && (
            <Box 
              component="img" 
              src={value}
              alt="Bank logo"
              sx={{ height: 40, width: 'auto', mr: 1 }}
            />
          )}
          <Button 
            onClick={() => setShowUrlInput(true)}
            variant="outlined"
            size="small"
          >
            Add URL
          </Button>
          <Button
            onClick={() => fileInputRef.current.click()}
            variant="outlined"
            size="small"
          >
            Upload
          </Button>
          {value && (
            <IconButton 
              size="small" 
              color="error"
              onClick={() => onChange(null)}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      )}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept="image/*"
        style={{ display: 'none' }}
      />
    </Box>
  );
};

const WalletCard = ({ wallet, onEdit, onDelete, onArchive, primaryCard, allWallets }) => {
  const icon = walletTypes.find(type => type.value === wallet.type)?.icon;
  
  // Update the getCardLimits function
  const getCardLimits = (currentWallet) => {
    if (currentWallet.type !== 'credit') return null;

    // Get the primary card (either self or linked primary)
    const cardPrimary = currentWallet.isPrimary 
      ? currentWallet 
      : allWallets.find(w => w.id === currentWallet.primaryCardId);

    if (!cardPrimary) return null;

    // Get individual card's used amount (negative balance means used amount)
    const individualUsed = currentWallet.balance < 0 ? Math.abs(currentWallet.balance) : 0;

    // Get all cards linked to this primary card (including primary)
    const linkedCards = allWallets.filter(w => 
      w.id === cardPrimary.id || w.primaryCardId === cardPrimary.id
    );

    // Calculate total used limit across all linked cards
    const totalUsedLimit = linkedCards.reduce((total, card) => 
      total + (card.balance < 0 ? Math.abs(card.balance) : 0), 0
    );

    // Get credit limit from primary card
    const creditLimit = cardPrimary.creditLimit || 0;

    // Calculate available limit
    const availableLimit = Math.max(0, creditLimit - totalUsedLimit);

    // Calculate utilization percentage
    const utilizationPercentage = creditLimit > 0 
      ? (totalUsedLimit / creditLimit) * 100 
      : 0;

    return {
      used: individualUsed,
      available: availableLimit,
      total: creditLimit,
      utilization: utilizationPercentage
    };
  };

  const getBankLogo = () => {
    if (wallet.type !== 'bank') return null;
    if (wallet.bankId === 'custom') return wallet.customBankLogo;
    return bankOptions.find(bank => bank.value === wallet.bankId)?.logo;
  };

  return (
    <StyledCard type={wallet.type} variant={wallet.cardVariant}>
      {wallet.type === 'credit' && logos.cards[wallet.cardVariant] && (
        <CardWatermark logo={logos.cards[wallet.cardVariant]} />
      )}
      {wallet.type !== 'credit' && (
        <Box sx={{
          position: 'absolute',
          right: -20,
          bottom: -20,
          opacity: 0.06,
          zIndex: 0,
          pointerEvents: 'none',
          lineHeight: 0,
          '& svg': { fontSize: 150 }
        }}>
          {walletTypes.find(type => type.value === wallet.type)?.icon}
        </Box>
      )}
      <CardContent sx={{ 
        position: 'relative',
        zIndex: 1,
        p: { xs: 1.5, sm: 2 },
        '&:last-child': { pb: { xs: 1.5, sm: 2 } }
      }}>
        {wallet.type === 'credit' ? (
          <>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: { xs: 1, sm: 1.5 }
            }}>
              
              <CardNetworkLogo variant={wallet.cardVariant} sx={{ ml: 'auto' }} />
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                color: 'inherit',
                fontSize: '0.875rem',
                mb: 0.5
              }}
            >
              {wallet.name}
              {wallet.isPrimary && (
                <Typography
                  component="span"
                  sx={{
                    ml: 0.5,
                    fontSize: '0.6rem',
                    bgcolor: alpha('#fff', 0.2),
                    color: 'inherit',
                    px: 0.6,
                    py: 0.1,
                    borderRadius: 1
                  }}
                >
                  Primary
                </Typography>
              )}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                mb: 1.5,
                display: 'block',
                fontFamily: 'monospace',
                letterSpacing: 1,
                color: alpha('#fff', 0.8)
              }}
            >
              •••• {wallet.accountNumber?.slice(-4) || '****'}
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Typography sx={{ 
                fontSize: '1.1rem', 
                fontWeight: 600,
                color: 'inherit'
              }}>
                {formatCurrency(getCardLimits(wallet)?.total || 0)}
              </Typography>
              <Typography variant="caption" sx={{ 
                opacity: 0.8,
                color: 'inherit',
                display: 'block',
                mb: 1
              }}>
                {!wallet.isPrimary && wallet.primaryCardId && primaryCard 
                  ? `Shared Credit Limit (${primaryCard.name})` 
                  : "Total Credit Limit"}
              </Typography>
            </Box>

            {getCardLimits(wallet) && (
              <>
                <Box sx={{ 
                  width: '100%', 
                  height: 3,
                  bgcolor: alpha('#fff', 0.1),
                  borderRadius: 2,
                  mb: 1
                }}>
                  <Box sx={{ 
                    width: `${Math.min(getCardLimits(wallet).utilization, 100)}%`,
                    height: '100%',
                    bgcolor: getCardLimits(wallet).utilization > 30 
                      ? alpha('#ff4444', 0.9)
                      : alpha('#4CAF50', 0.9),
                    borderRadius: 2,
                    transition: 'width 0.5s ease-in-out'
                  }} />
                </Box>

                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ 
                      color: alpha('#fff', 0.8),
                      display: 'block'
                    }}>
                      Used
                    </Typography>
                    <Typography sx={{ 
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: alpha('#fff', 0.95)
                    }}>
                      {formatCurrency(getCardLimits(wallet).used)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ 
                      color: alpha('#fff', 0.8),
                      display: 'block'
                    }}>
                      Available
                    </Typography>
                    <Typography sx={{ 
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: alpha('#fff', 0.95)
                    }}>
                      {formatCurrency(getCardLimits(wallet).available)}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}
          </>
        ) : (
          <>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: { xs: 1, sm: 1.5 }
            }}>
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#fff', 0.15),
                color: '#fff',
                '& svg': { fontSize: 20 }
              }}>
                {walletTypes.find(type => type.value === wallet.type)?.icon}
              </Box>
              {getBankLogo() ? (
                <Box sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#fff',
                  borderRadius: 1.5,
                  px: 1,
                  height: 32,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
                }}>
                  <BankLogo
                    src={getBankLogo()}
                    alt={bankOptions.find(bank => bank.value === wallet.bankId)?.label || 'Bank logo'}
                    onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                    sx={{ mb: 0, ml: 0, height: 18, maxWidth: 80 }}
                  />
                </Box>
              ) : (
                <Typography component="span" sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: alpha('#fff', 0.15)
                }}>
                  {wallet.type === 'bank' ? 'Bank' : 'Cash'}
                </Typography>
              )}
            </Box>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'inherit',
                fontSize: '0.875rem',
                mb: 0.5
              }}
            >
              {wallet.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                mb: 1.5,
                display: 'block',
                fontFamily: wallet.accountNumber ? 'monospace' : 'inherit',
                letterSpacing: wallet.accountNumber ? 1 : 0,
                color: alpha('#fff', 0.8),
                minHeight: 20
              }}
            >
              {wallet.accountNumber
                ? `•••• ${wallet.accountNumber.slice(-4)}`
                : wallet.type === 'cash' ? 'Cash in hand' : ''}
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Typography sx={{
                fontSize: '1.5rem',
                fontWeight: 700,
                letterSpacing: '-0.01em',
                color: 'inherit'
              }}>
                {formatCurrency(wallet.balance)}
              </Typography>
              <Typography variant="caption" sx={{
                opacity: 0.8,
                color: 'inherit',
                display: 'block'
              }}>
                Available Balance
              </Typography>
            </Box>

            {wallet.type === 'bank' && bankOptions.find(bank => bank.value === wallet.bankId) && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 1.5,
                  color: alpha('#fff', 0.75)
                }}
              >
                {bankOptions.find(bank => bank.value === wallet.bankId).label}
              </Typography>
            )}
          </>
        )}
      </CardContent>
      <CardActions sx={{ 
        position: 'relative',
        zIndex: 1,
        justifyContent: 'flex-end', 
        p: 1,
        borderTop: '1px solid',
        borderColor: alpha('#fff', 0.12),
        '& .MuiIconButton-root': {
          color: alpha('#fff', 0.85),
          '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.12) }
        }
      }}>
        <IconButton 
          size="small"
          onClick={() => onEdit(wallet)}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton 
          size="small"
          onClick={() => onArchive(wallet)}
          sx={wallet.is_archived ? { '&&': { color: '#86efac' } } : undefined}
        >
          {wallet.is_archived ? <RestoreIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
        </IconButton>
        <IconButton 
          size="small"
          onClick={() => onDelete(wallet.id)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </CardActions>
    </StyledCard>
  );
};

const WalletDialog = ({ open, onClose, wallet, onSave, primaryCards }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'bank',
    balance: 0,
    creditLimit: 0,
    accountNumber: '',
    cardVariant: 'visa',
    isPrimary: false,
    primaryCardId: null,
    bankId: '',
    customBankLogo: null,
    notes: ''
  });

  const { supabase } = useSupabase();

  useEffect(() => {
    if (wallet) {
      setFormData(wallet);
    } else {
      setFormData({
        name: '',
        type: 'bank',
        balance: 0,
        creditLimit: 0,
        accountNumber: '',
        cardVariant: 'visa',
        isPrimary: false,
        primaryCardId: null,
        bankId: '',
        customBankLogo: null,
        notes: ''
      });
    }
  }, [wallet]);

  const handleChange = (e) => {
    const value = e.target.type === 'number' ? parseFloat(e.target.value) : 
                 e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    
    // Reset primaryCardId if card is marked as primary
    if (e.target.name === 'isPrimary' && value === true) {
      setFormData({ ...formData, isPrimary: true, primaryCardId: null });
    } else if (e.target.name === 'type' && value !== 'credit') {
      // Reset credit card related fields when type is changed to non-credit
      setFormData({
        ...formData,
        [e.target.name]: value,
        cardVariant: null,
        isPrimary: false,
        primaryCardId: null,
        creditLimit: 0
      });
    } else {
      setFormData({ ...formData, [e.target.name]: value });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Convert balance to negative for credit cards
    const dataToSave = { ...formData };
    if (dataToSave.type === 'credit' && dataToSave.balance > 0) {
      dataToSave.balance = -Math.abs(dataToSave.balance);
    }
    onSave(dataToSave);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={useMediaQuery((theme) => theme.breakpoints.down('sm'))}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {wallet ? 'Edit Wallet' : 'Add New Wallet'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Wallet Name"
                fullWidth
                required
                value={formData.name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                name="type"
                label="Wallet Type"
                fullWidth
                required
                value={formData.type}
                onChange={handleChange}
              >
                {walletTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            {/* Only show credit card specific fields when type is credit */}
            {formData.type === 'credit' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    select
                    name="cardVariant"
                    label="Card Type"
                    fullWidth
                    required
                    value={formData.cardVariant}
                    onChange={handleChange}
                  >
                    {cardVariants.map((variant) => (
                      <MenuItem key={variant.value} value={variant.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CardNetworkLogo variant={variant.value} size="small" />
                          {variant.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography>Is Primary Card?</Typography>
                    <Switch
                      name="isPrimary"
                      checked={formData.isPrimary}
                      onChange={handleChange}
                    />
                  </Box>
                </Grid>
                {!formData.isPrimary && primaryCards.length > 0 && (
                  <Grid item xs={12}>
                    <TextField
                      select
                      name="primaryCardId"
                      label="Link to Primary Card"
                      fullWidth
                      value={formData.primaryCardId || ''}
                      onChange={handleChange}
                    >
                      <MenuItem value="">None</MenuItem>
                      {primaryCards.map((card) => (
                        <MenuItem key={card.id} value={card.id}>
                          {card.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="creditLimit"
                    label="Credit Limit"
                    type="number"
                    fullWidth
                    required
                    disabled={!formData.isPrimary && formData.primaryCardId}
                    value={formData.creditLimit}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                    }}
                    helperText={!formData.isPrimary && formData.primaryCardId ? "Uses primary card's limit" : ""}
                    sx={numberInputStyle}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={formData.type === 'credit' ? 6 : 12}>
              <TextField
                name="balance"
                label={formData.type === 'credit' ? 'Current Outstanding' : 'Current Balance'}
                type="number"
                fullWidth
                required
                value={formData.type === 'credit' ? Math.abs(formData.balance) : formData.balance}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
                  inputProps: { min: 0 } // Ensure non-negative input
                }}
                sx={numberInputStyle}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="accountNumber"
                label="Account Number"
                fullWidth
                value={formData.accountNumber}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes"
                fullWidth
                multiline
                rows={2}
                value={formData.notes}
                onChange={handleChange}
              />
            </Grid>

            {formData.type === 'bank' && (
              <>
                {formData.bankId === 'custom' && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Custom Bank Logo
                    </Typography>
                    <LogoManagement
                      value={formData.customBankLogo}
                      onChange={(url) => 
                        setFormData({ ...formData, customBankLogo: url })
                      }
                      supabase={supabase}
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">Save</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// Add these sorting options
const sortOptions = [
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
  { value: 'balance_high', label: 'Balance (High-Low)' },
  { value: 'balance_low', label: 'Balance (Low-High)' },
  { value: 'recent', label: 'Most Recent' },
  { value: 'oldest', label: 'Oldest' }
];

// Add archive status options
const archiveStatusOptions = [
  { value: 'active', label: 'Active Wallets' },
  { value: 'archived', label: 'Archived Wallets' },
  { value: 'all', label: 'All Wallets' }
];

// Add this helper function at the component level
const getWalletBalance = (wallet, allWallets) => {
  if (wallet.type === 'credit') {
    // For credit cards, use available balance
    const primaryCard = wallet.primaryCardId ? 
      allWallets.find(w => w.id === wallet.primaryCardId) : wallet;
    
    // Calculate total used limit across all linked cards
    const linkedCards = allWallets.filter(w => 
      w.primaryCardId === primaryCard.id || w.id === primaryCard.id
    );
    
    const totalUsedLimit = linkedCards.reduce((total, card) => 
      total + (card.balance < 0 ? Math.abs(card.balance) : 0), 0
    );
    
    // Use primary card's credit limit for all linked cards
    const creditLimit = primaryCard.creditLimit;
    
    return creditLimit - totalUsedLimit; // Return available balance
  }
  // For other wallet types, use regular balance
  return wallet.balance;
};

const Wallets = () => {
  const [wallets, setWallets] = useState([]);
  const [filteredWallets, setFilteredWallets] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name_asc');
  const [filterType, setFilterType] = useState('all');
  const walletsTour = useTour('wallets');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [walletToDelete, setWalletToDelete] = useState(null);
  const [archiveStatus, setArchiveStatus] = useState('active'); // Default to show only active wallets

  const primaryCards = wallets.filter(w => w.type === 'credit' && w.isPrimary);

  const { supabase } = useSupabase();

  useEffect(() => {
    fetchWallets();
  }, []);

  const fetchWallets = async () => {
    try {
      const { data, error } = await supabase
        .from('wallets')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;

      // Convert snake_case to camelCase
      const formattedData = (data || []).map(wallet => ({
        id: wallet.id,
        userId: wallet.user_id,
        name: wallet.name,
        type: wallet.type,
        balance: wallet.balance,
        creditLimit: wallet.credit_limit,
        accountNumber: wallet.account_number,
        cardVariant: wallet.card_variant,
        isPrimary: wallet.is_primary,
        primaryCardId: wallet.primary_card_id,
        notes: wallet.notes,
        createdAt: wallet.created_at,
        updatedAt: wallet.updated_at,
        is_archived: wallet.is_archived || false
      }));

      setWallets(formattedData);
    } catch (error) {
      console.error('Error fetching wallets:', error);
    }
  };

  const handleAddWallet = () => {
    setSelectedWallet(null);
    setDialogOpen(true);
  };

  const handleEditWallet = (wallet) => {
    setSelectedWallet(wallet);
    setDialogOpen(true);
  };

  const handleDeleteClick = (walletId) => {
    const walletToDelete = wallets.find(w => w.id === walletId);
    setWalletToDelete(walletToDelete);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!walletToDelete) return;

    try {
      // First delete any associated credit card details
      if (walletToDelete.type === 'credit') {
        const { error: detailsError } = await supabase
          .from('credit_card_details')
          .delete()
          .eq('wallet_id', walletToDelete.id);

        if (detailsError) throw detailsError;
      }

      // Then delete the wallet
      const { error } = await supabase
        .from('wallets')
        .delete()
        .eq('id', walletToDelete.id);

      if (error) throw error;

      // Update local state
      setWallets(wallets.filter(w => w.id !== walletToDelete.id));
      setDeleteConfirmOpen(false);
      setWalletToDelete(null);
    } catch (error) {
      console.error('Error deleting wallet:', error);
      alert('Failed to delete wallet. Please try again.');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setWalletToDelete(null);
  };

  const handleSaveWallet = async (formData) => {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('Error getting user:', userError);
        throw userError;
      }

      if (!user) {
        console.error('No user found');
        return;
      }

      console.log('Saving wallet with data:', formData); // Debug log

      // Base wallet data
      const walletData = {
        user_id: user.id,
        name: formData.name,
        type: formData.type,
        balance: formData.balance,
        account_number: formData.accountNumber,
        notes: formData.notes
      };

      // Add credit card specific fields only if type is credit
      if (formData.type === 'credit') {
        Object.assign(walletData, {
          credit_limit: formData.creditLimit,
          card_variant: formData.cardVariant,
          is_primary: formData.isPrimary,
          primary_card_id: formData.primaryCardId
        });
      }

      console.log('Processed wallet data:', walletData); // Debug log

      if (selectedWallet) {
        console.log('Updating wallet with ID:', selectedWallet.id); // Debug log
        const { data, error } = await supabase
          .from('wallets')
          .update(walletData)
          .eq('id', selectedWallet.id)
          .select();

        if (error) {
          console.error('Update error:', error);
          throw error;
        }
        console.log('Update response:', data); // Debug log
      } else {
        console.log('Inserting new wallet'); // Debug log
        const { data, error } = await supabase
          .from('wallets')
          .insert([walletData])
          .select();

        if (error) {
          console.error('Insert error:', error);
          throw error;
        }
        console.log('Insert response:', data); // Debug log
      }

      setDialogOpen(false);
      fetchWallets();
    } catch (error) {
      console.error('Error saving wallet:', error);
      console.error('Error details:', error.message, error.details, error.hint); // More detailed error logging
      alert(`Error saving wallet: ${error.message}`); // Show error to user
    }
  };

  // Filter and sort wallets
  const getFilteredAndSortedWallets = () => {
    let filtered = [...wallets];

    // Apply archive status filter first
    if (archiveStatus === 'active') {
      filtered = filtered.filter(wallet => !wallet.is_archived);
    } else if (archiveStatus === 'archived') {
      filtered = filtered.filter(wallet => wallet.is_archived);
    }
    // 'all' status doesn't need filtering

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(wallet => 
        wallet.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        wallet.accountNumber?.includes(searchQuery)
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(wallet => wallet.type === filterType);
    }

    // Apply sorting
    switch (sortBy) {
      case 'name_asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name_desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'balance_high':
        filtered.sort((a, b) => 
          getWalletBalance(b, wallets) - getWalletBalance(a, wallets)
        );
        break;
      case 'balance_low':
        filtered.sort((a, b) => 
          getWalletBalance(a, wallets) - getWalletBalance(b, wallets)
        );
        break;
      case 'recent':
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      default:
        break;
    }

    return filtered;
  };

  // Group wallets by type, respecting the archive status filter
  const groupedWallets = getFilteredAndSortedWallets().reduce((groups, wallet) => {
    const type = wallet.type;
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(wallet);
    return groups;
  }, {});

  // Add useEffect to handle filter changes
  useEffect(() => {
    const filteredWallets = getFilteredAndSortedWallets();
    setFilteredWallets(filteredWallets);
  }, [wallets, searchQuery, filterType, sortBy, archiveStatus]);

  // Archive status options
  const archiveStatusOptions = [
    { value: 'active', label: 'Active Wallets' },
    { value: 'archived', label: 'Archived Wallets' },
    { value: 'all', label: 'All Wallets' }
  ];

  // Add handler for archive status change
  const handleArchiveStatusChange = (e) => {
    setArchiveStatus(e.target.value);
  };

  // Calculate totals
  const getTotals = () => {
    return wallets.reduce((totals, wallet) => {
      if (!totals[wallet.type]) {
        totals[wallet.type] = 0;
      }
      totals[wallet.type] += wallet.balance;
      totals.total = (totals.total || 0) + wallet.balance;
      return totals;
    }, {});
  };

  const totals = getTotals();

  // Add handleArchive function
  const handleArchive = async (wallet) => {
    try {
      const { error } = await supabase
        .from('wallets')
        .update({ is_archived: !wallet.is_archived })
        .eq('id', wallet.id);

      if (error) throw error;
      fetchWallets();
    } catch (error) {
      console.error('Error archiving wallet:', error);
    }
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2, md: 4 },
      minHeight: '100vh'
    }}>
      <PageHeader>
        <Box>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              fontSize: { xs: '1.5rem', sm: '2rem' },
              color: 'text.primary',
              mb: 1
            }}
          >
            Wallets
          </Typography>
          <Typography
            variant="body1"
            data-tour="wallets-total"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              display: 'inline-block'
            }}
          >
            Total Balance: {formatCurrency(totals.total || 0)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TourButton onClick={walletsTour.start} />
          <AddButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddWallet}
            data-tour="wallets-add"
          >
            Add Wallet
          </AddButton>
        </Box>
      </PageHeader>

      <Stack
        direction="row"
        spacing={1}
        data-tour="wallets-types"
        sx={{
          mb: 2,
          overflowX: 'auto',
          flexWrap: { xs: 'nowrap', sm: 'wrap' },
          pb: { xs: 1, sm: 0 },
          mx: { xs: -1, sm: 0 },
          px: { xs: 1, sm: 0 },
          '&::-webkit-scrollbar': {
            display: { xs: 'none', sm: 'auto' }
          }
        }}
      >
        {walletTypes.map(type => (
          <StyledChip
            key={type.value}
            label={`${type.label}: ${formatCurrency(totals[type.value] || 0)}`}
            icon={type.icon}
            onClick={() => setFilterType(filterType === type.value ? 'all' : type.value)}
            selected={filterType === type.value}
          />
        ))}
      </Stack>

      <SearchBar data-tour="wallets-filters">
        <StyledOutlinedInput
          placeholder="Search wallets..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          }
          size="small"
          fullWidth
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value={archiveStatus}
            onChange={handleArchiveStatusChange}
            input={<StyledOutlinedInput />}
            displayEmpty
          >
            {archiveStatusOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            input={<StyledOutlinedInput />}
            displayEmpty
            startAdornment={
              <InputAdornment position="start">
                <SortIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            }
          >
            {sortOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </SearchBar>

      {filterType === 'all' ? (
        Object.entries(groupedWallets).map(([type, typeWallets]) => (
          typeWallets.length > 0 && (
            <Box 
              key={type} 
              sx={{ 
                mb: { xs: 3, sm: 6 },
                background: theme => alpha(theme.palette.background.paper, 0.4),
                borderRadius: { xs: '16px', sm: '24px' },
                p: { xs: 2, sm: 3 },
                backdropFilter: 'blur(10px)',
                boxShadow: 'var(--fx-card-shadow)'
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3,
                  fontWeight: 600,
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                {walletTypes.find(t => t.value === type)?.icon}
                {walletTypes.find(t => t.value === type)?.label}
                <Typography 
                  component="span" 
                  sx={{ 
                    ml: 'auto',
                    color: 'text.secondary',
                    fontWeight: 500
                  }}
                >
                  {formatCurrency(totals[type] || 0)}
                </Typography>
              </Typography>
              <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
                {typeWallets.map(wallet => (
                  <Grid item xs={12} sm={6} md={4} key={wallet.id} data-tour="wallets-card">
                    <WalletCard 
                      wallet={wallet}
                      onEdit={handleEditWallet}
                      onDelete={handleDeleteClick}
                      onArchive={handleArchive}
                      primaryCard={wallet.primaryCardId ? 
                        wallets.find(w => w.id === wallet.primaryCardId) : null}
                      allWallets={wallets}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )
        ))
      ) : (
        <Box sx={{ 
          background: theme => alpha(theme.palette.background.paper, 0.4),
          borderRadius: '24px',
          p: 3,
          backdropFilter: 'blur(10px)',
          boxShadow: 'var(--fx-card-shadow)'
        }}>
          <Grid container spacing={3}>
            {filteredWallets.map(wallet => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={wallet.id} data-tour="wallets-card">
                <WalletCard 
                  wallet={wallet}
                  onEdit={handleEditWallet}
                  onDelete={handleDeleteClick}
                  onArchive={handleArchive}
                  primaryCard={wallet.primaryCardId ? 
                    wallets.find(w => w.id === wallet.primaryCardId) : null}
                  allWallets={wallets}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: theme => `linear-gradient(135deg, 
              ${alpha(theme.palette.background.paper, 0.95)}, 
              ${alpha(theme.palette.background.paper, 1)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: theme => `0 24px 48px -12px ${alpha(theme.palette.common.black, 0.25)}`
          }
        }}
      >
        <WalletDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          wallet={selectedWallet}
          onSave={handleSaveWallet}
          primaryCards={primaryCards}
        />
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete {walletToDelete?.name}? This action cannot be undone.
            {walletToDelete?.type === 'credit' && (
              <Typography 
                color="error" 
                sx={{ mt: 2, display: 'block' }}
              >
                Warning: Deleting this credit card will also remove all associated transaction history.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteCancel}
            color="inherit"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            sx={{ 
              borderRadius: 2,
              px: 2,
              '&:hover': {
                bgcolor: 'error.dark'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <GuidedTour steps={TOURS.wallets} open={walletsTour.open} onClose={walletsTour.close} />
    </Box>
  );
};

export default Wallets;